import { useContext, useRef } from 'react';
import { Stripe } from '@stripe/stripe-js';
import { IFlexNavigateAction } from './useFlexNavigateAction';
import { FlexContext } from '../../contexts/flexContext';
import useOverlay from '../useOverlay';
import useLanguage from '../useLanguage';
import useFlexNavigation from '../flex/useFlexNavigation';
import request from '../../utils/request';

export interface IStripeAttemptResult {
  paymentIntentId: string;
  intentStatus:
    | 'succeeded'
    | 'requires_action'
    | 'requires_capture'
    | 'requires_confirmation'
    | 'canceled';
  clientSecret: string;
  confirmRequiredActionUrl: string;
}

export interface IStripeAttemptPaymentParams {
  attemptUrl: string;
  relayPayload: object;
  paymentMethodId: string;
  shouldSaveCard: boolean;
}

// Shared by the card checkout overlay and the Apple Pay / Google Pay express checkout
// element: posts the created payment method to attemptUrl and drives the same
// confirm / 3D Secure / completion-navigation flow regardless of which UI collected it.
const useStripeAttemptPayment = () => {
  const flexContext = useContext(FlexContext);
  const overlay = useOverlay();
  const language = useLanguage();
  const initialHistoryLengthRef = useRef(window.history.length);

  const handleFlexNavigation = useFlexNavigation();

  const attemptPayment = (
    stripe: Stripe | null,
    {
      attemptUrl,
      relayPayload,
      paymentMethodId,
      shouldSaveCard,
    }: IStripeAttemptPaymentParams
  ) => {
    return new Promise<IFlexNavigateAction>((resolve, reject) => {
      const handleCompletionNavigation = () => {
        if (flexContext.node?.type === 'payment') {
          handleFlexNavigation(flexContext.node.completionNavigation);
        }
      };

      const confirmIntent = (confirmUrl: string, intentId: string) => {
        request<IStripeAttemptResult>(confirmUrl, {
          method: 'POST',
          body: {
            intent_id: intentId,
          },
        })
          .then((confirmIntentResult) => {
            switch (confirmIntentResult.intentStatus) {
              case 'succeeded':
              case 'requires_capture':
                handleCompletionNavigation();
                break;

              case 'canceled':
                // Note: This message is hard coded in the apps as well.
                overlay.presentBasicAlert({
                  message: 'Payment cancelled',
                });
                break;

              default:
                // Note: This message is hard coded in the apps as well.
                overlay.presentBasicAlert({
                  message: 'Cannot confirm payment intent',
                });
                break;
            }
          })
          .catch((error) => reject(error));
      };

      const retrievePaymentIntent = (
        clientSecret: string,
        confirmUrl: string
      ) => {
        const on3DSComplete = (e: MessageEvent) => {
          if (e.data === '3ds-authentication-complete') {
            window.removeEventListener('message', on3DSComplete);

            const iframeHistoryLength =
              window.history.length - initialHistoryLengthRef.current;

            // Any navigation within the iframe also affects the main window browser history.
            // As the 3D Secure flow has it's own redirects, we need to negate them
            // before closing the overlay and destroying the iframe.
            if (iframeHistoryLength > 0) {
              window.history.go(iframeHistoryLength * -1);
            }

            setTimeout(() => {
              overlay.dismiss();
              retrievePaymentIntent(clientSecret, confirmUrl);
            }, 300);
          }
        };

        stripe
          ?.retrievePaymentIntent(clientSecret)
          .then(({ paymentIntent }) => {
            if (paymentIntent) {
              // Sigh. If the Stripe API version used predates 2019-02-11, 'requires_action'
              // appears as 'requires_source_action'. This of course does not match Stripe.js type definitions,
              // hence casting the status to string.
              switch (paymentIntent.status as string) {
                case 'succeeded':
                case 'requires_capture':
                  handleCompletionNavigation();
                  break;

                case 'requires_confirmation':
                  confirmIntent(confirmUrl, paymentIntent.id);
                  break;

                case 'requires_source_action':
                  const url = paymentIntent?.next_action?.redirect_to_url?.url;

                  if (url) {
                    window.addEventListener('message', on3DSComplete);

                    overlay.presentIFrame({
                      urlSource: url,
                      closeButtonLabel: language.get('cancel'),
                      onDismiss: () => {
                        window.removeEventListener('message', on3DSComplete);
                      },
                    });
                  } else {
                    reject();
                  }
                  break;

                case 'requires_source':
                  overlay.presentBasicAlert({
                    message: 'Payment cancelled',
                  });
                  break;
                default:
                  reject();
                  break;
              }
            } else {
              reject();
            }
          })
          .catch((error) => reject(error));
      };

      request<IStripeAttemptResult>(attemptUrl, {
        method: 'POST',
        body: {
          payment_method_id: paymentMethodId,
          save_card: shouldSaveCard,
          return_url: `${window.location.origin}/stripe-redirect.html`,
          payload: relayPayload,
        },
      })
        .then((attemptResult: IStripeAttemptResult) => {
          switch (attemptResult.intentStatus) {
            case 'requires_confirmation':
              confirmIntent(
                attemptResult.confirmRequiredActionUrl,
                attemptResult.paymentIntentId
              );
              break;

            case 'requires_action':
              retrievePaymentIntent(
                attemptResult.clientSecret,
                attemptResult.confirmRequiredActionUrl
              );
              break;

            case 'requires_capture':
            case 'succeeded':
              handleCompletionNavigation();
              break;
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  return attemptPayment;
};

export default useStripeAttemptPayment;
